import React, { ReactElement, useContext, useEffect, useState } from "react";
import { AuthContext } from "../contexts/authContext";
import { LeaderboardMovie } from "../models/leaderboard-movie";
import tmdbLogo from "../assets/tmdb_small.svg";
import starIcon from "../assets/star.svg";
import { ALL_GENRES } from "../models/genre";
import { useNavigate } from "react-router-dom";

export default function Leaderboard() {
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);
  const [leaderboard, setLeaderboard] = useState<LeaderboardMovie[]>([]);
  const [selectedGenre, setSelectedGenre] = useState<number>(0);

  async function fetchLeaderboard(genreId: number) {
    if (authContext && authContext.authApiClient) {
      let url = "/leaderboard";
      if (genreId != 0){
        url += "/genre/" + genreId;
      }
      const response =
        await authContext.authApiClient.get<LeaderboardMovie[]>(url);
      if (response.status === 200) {
        setLeaderboard(response.data.sort((a, b) => b.rating - a.rating));
      }
    }
  }

  function getYear(date: string): number {
    return new Date(date).getFullYear();
  }

  useEffect(() => {
    fetchLeaderboard(selectedGenre);
  }, [selectedGenre]);

  function getGenreNameWithPronoun(): ReactElement {
    const vowels = ["a", "e", "i", "o", "u"];
    const name = ALL_GENRES.find(g => g.id === selectedGenre)?.name.toLowerCase() || "";
    const pronoun = vowels.some(vowel => vowel === name[0]) ? "an" : "a";
    return (<span>{pronoun + " " }<span className="text-orange-300">{name}</span></span>);
  }

  return (
    <div className="flex flex-col max-w-7xl mx-auto mt-16 px-2 sm:px-4">
      <div className="self-center mb-10 flex flex-col md:flex-row items-center uppercase">
        <span className="text-xl sm:text-4xl text-orange-300">Your top</span>
        <select onChange={(e) => setSelectedGenre(parseInt(e.target.value))} className="px-4 py-2 mx-1 sm:text-2xl rounded text-orange-300 w-full md:w-auto font-bold border border-solid border-orange-300 bg-purple-900 text-center uppercase">
          {ALL_GENRES.map(g => (<option key={g.id} value={g.id}>{g.name}</option>))}
        </select>
        <span className="text-xl sm:text-4xl text-orange-300">movies</span>
      </div>
      <div className="flex flex-col">
        {leaderboard.map((mov, index) => {
          const isFirst = index === 0;
          return (
            <div
              key={mov.movie.id}
              className={
                "flex flex-col p-2 sm:p-4 relative " +
                (isFirst ? "mb-8 bg-opacity-20 bg-black" : "")
              }
            >
              <div className="flex flex-row">
                <div className="w-10 sm:w-24 flex-shrink-0">
                  <span className={"text-3xl sm:text-5xl " + (isFirst ? "active" : "")}>
                    {index + 1}.
                  </span>
                </div>
                <div className="flex flex-row gap-2 flex-1 min-w-0">
                  <img
                    className={(isFirst ? "w-24 sm:w-48" : "w-16 sm:w-24") + " flex-shrink-0 object-cover"}
                    alt={`poster for movie ${mov.movie.title}`}
                    src={
                      "http://image.tmdb.org/t/p/w342/" + mov.movie.poster_path
                    }
                  />
                  <div className="flex flex-col px-1 sm:px-2 min-w-0">
                    <div className="text-base sm:text-2xl uppercase font-bold leading-tight sm:leading-normal">
                      {mov.movie.title}
                    </div>
                    <div className="text-sm sm:text-base">{getYear(mov.movie.release_date)}</div>
                    {isFirst ? (
                      <div className="mt-4 sm:block overflow-hidden line-clamp-5 md:line-clamp-8 text-xs md:text-base">{mov.movie.overview}</div>
                    ) : null}
                  </div>
                </div>
              </div>
              <div className="flex flex-row gap-6 mt-3 ml-10 sm:ml-0 sm:mt-0 sm:absolute sm:right-4 sm:top-4">
                <div className="text-center">
                  <img className="w-10 sm:w-14 mx-auto" alt="tmdb logo" src={tmdbLogo} />
                  <div className="flex flex-row content-center justify-center">
                    <div>{mov.movie.vote_average.toFixed(1)}</div>
                    <div className="flex items-center">
                      <img className="w-4" alt="star icon" src={starIcon} />
                    </div>
                  </div>
                </div>
                <div className="flex flex-col">
                  <div>{mov.rating.toFixed(2)}</div>
                  <div className="flex flex-row text-gray-400">
                    <div className="mr-4">
                      {mov.wins}
                      <span className="text-xs">W</span>
                    </div>
                    <div>
                      {mov.losses}
                      <span className="text-xs">L</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
        { leaderboard.length === 0 ? (
          <div className="self-center flex flex-col items-center">
            <span className="my-2 text-2xl font-bold text-orange-300 uppercase">It's empty in here...</span>
            <span className="text-xl">The leaderboard only shows movies with at least 3 votes.</span>
            <span className="text-xl">It looks like you have none</span>
            { selectedGenre != 0 ? (
              <span className="text-sm">Not much of { getGenreNameWithPronoun() } fan are you?</span>
            ) : null }
            
            <button 
              onClick={() => navigate("/vote")}
              className="btn-primary px-8 py-3 mt-4 uppercase">Go Vote!</button>
          </div>
        ) : null}
      </div>
    </div>
  );
}